import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowDownIcon,
  ArrowsSplitIcon,
  ArrowUpIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  FunnelSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  assignCategoryColors,
  type BudgetCategory,
  CODE_ONLY_CATEGORIES,
  formatCurrency,
  formatDate,
  getCategoryColor,
  splitCategoryEmoji,
} from '@budget/core';
import { apiFetch } from '../auth/apiFetch';
import { useHousehold } from '../auth/useHousehold';
import { CategoryBadge } from '../components/CategoryBadge';
import { Checkbox } from '../components/Checkbox';
import { type DropdownOption } from '../components/Dropdown';
import { IconButton } from '../components/IconButton';
import { Modal, ModalFooter } from '../components/Modal';
import { Select } from '../components/Select';
import { useScope } from '../hooks/useScope';
import { ThirdPartyLogo } from '../components/ThirdPartyLogos';
import { getPersonName } from '../config/personNames';
import { useTransactions, type Transaction, type TransactionsScope } from '../data/useTransactions';
import { usePlaidItems } from '../data/usePlaidItems';
import { useDashboard } from '../data/useDashboard';
import { bumpActualsVersion } from '../data/actualsBus';
import { currentEtMonthKey } from '../lib/dates';
import clsx from 'clsx';

function parseMonthKey(key: string): { year: number; month0: number } {
  const [y, m] = key.split('-').map(Number);
  return { year: y, month0: (m ?? 1) - 1 };
}

function monthRange(key: string): { from: string; to: string; label: string } {
  const { year, month0 } = parseMonthKey(key);
  const lastDay = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const mm = String(month0 + 1).padStart(2, '0');
  return {
    from: `${year}-${mm}-01`,
    to: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
    label: formatDate(new Date(Date.UTC(year, month0, 15)), {
      month: 'long',
      year: 'numeric',
    }),
  };
}

function shiftMonth(key: string, delta: number): string {
  const { year, month0 } = parseMonthKey(key);
  const d = new Date(Date.UTC(year, month0 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// Display-only: budget categories from the sheet often have parenthetical
// notes ("Groceries (joint)", "Phone (split)"). Strip them for display, but
// the canonical value with parens is what stays stored in budget_category
// and matches against rules.
function displayCategory(name: string): string {
  return name.replace(/\s*\([^)]*\)/g, '').trim();
}

// Normalize a category name aggressively for matching only — strip
// leading emoji, strip parenthetical disambiguators, collapse whitespace,
// and apply Unicode NFC. This stays *agnostic* to whatever the user
// types inside the parens in the sheet ("(joint)", "(Clothing, Amazon,
// etc.)", anything) so legacy bare-form stored values still resolve
// when the sheet item gets renamed.
function normalizeCategoryForMatch(s: string): string {
  const noEmoji = splitCategoryEmoji(s).name;
  const noParens = noEmoji.replace(/\s*\([^)]*\)/g, '');
  return noParens.replace(/\s+/g, ' ').trim().normalize('NFC');
}

// If `raw` matches a sheet item only after normalization, return the
// canonical raw item name (with parens). Auto-heals legacy stored
// values like "Shopping & Miscellaneous" when the sheet's canonical
// item is "Shopping & Miscellaneous (Clothing, Amazon, etc.)" —
// otherwise the modal's strict-equality option lookup misses and
// renders the value as a top-level orphan instead of the real
// sub-item under its parent optgroup.
function canonicalizeCategory(
  raw: string,
  budgetGroups: { category: string; items: string[] }[],
): string {
  if (!raw) return raw;
  for (const g of budgetGroups) {
    if (g.items.includes(raw)) return raw;
  }
  const target = normalizeCategoryForMatch(raw);
  if (!target) return raw;
  for (const g of budgetGroups) {
    const match = g.items.find((i) => normalizeCategoryForMatch(i) === target);
    if (match) return match;
  }
  return raw;
}

type SortColumn = 'date' | 'merchant' | 'account' | 'category' | 'amount';
type SortDir = 'asc' | 'desc';

// `embedded` mounts the page as a tab inside another page (currently
// /budget?tab=transactions), skipping the outer page-container/header
// chrome. Scope is read from the global ScopeProvider in both modes, so
// the topbar toggle drives the view either way.
export function TransactionsPage({
  embedded = false,
}: {
  embedded?: boolean;
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const accountIdParam = searchParams.get('account');
  const accountId = accountIdParam ? Number(accountIdParam) : null;
  const todayKey = useMemo(() => currentEtMonthKey(), []);
  const monthKey = searchParams.get('month') ?? todayKey;
  const { from, to, label: monthLabel } = useMemo(() => monthRange(monthKey), [monthKey]);
  const isCurrentMonth = monthKey === todayKey;

  const setMonth = (newKey: string) => {
    const next = new URLSearchParams(searchParams);
    if (newKey === todayKey) next.delete('month');
    else next.set('month', newKey);
    setSearchParams(next, { replace: true });
  };

  const plaid = usePlaidItems();
  const household = useHousehold();
  const { scope } = useScope();
  // Map scope ('household' | 'dakota' | 'natalia') to the API's
  // TransactionsScope shape. `undefined` means "default to the caller" —
  // used for both the household scope and while a person's user_id is
  // still resolving (avoids sending viewAs=null mid-resolution).
  const txnsScope = useMemo<TransactionsScope | undefined>(() => {
    if (scope === 'household') return undefined;
    const viewAsUserId = scope === 'dakota' ? household.ids.dakota : household.ids.natalia;
    if (viewAsUserId == null) return undefined;
    return { viewAsUserId };
  }, [scope, household.ids]);
  const txns = useTransactions({ accountId, from, to, scope: txnsScope });
  // Viewing the OTHER household member's scope: every account is personal
  // (no joint accounts), so the other person's transactions are entirely
  // privacy-hidden from the caller. Surface a small notice so the empty
  // list doesn't read as missing data.
  const viewingOtherPerson =
    scope !== 'household' &&
    household.status === 'ready' &&
    household.selfId !== null &&
    household.ids[scope] !== null &&
    household.ids[scope] !== household.selfId;
  const dashboard = useDashboard();
  const [selectedTxnId, setSelectedTxnId] = useState<number | null>(null);
  const [sort, setSort] = useState<{ column: SortColumn; dir: SortDir }>({
    column: 'date',
    dir: 'desc',
  });

  const onSortClick = (column: SortColumn) => {
    setSort((prev) =>
      prev.column === column
        ? { column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : {
            column,
            // Date and Amount default to descending (recent / largest first);
            // text columns default to ascending (A→Z) on first click.
            dir: column === 'date' || column === 'amount' ? 'desc' : 'asc',
          },
    );
  };

  // Group budget items by their category for the recategorize <select>.
  // Sourced from the Cash Out tab via the dashboard store.
  const budgetGroups = useMemo(() => {
    const cats = dashboard.data?.budget.categories ?? [];
    return cats
      .map((c) => ({
        category: c.name,
        items: c.items.map((i) => i.name).filter((n) => n.length > 0),
      }))
      .filter((g) => g.items.length > 0);
  }, [dashboard.data]);

  const selectedTxn = useMemo(() => {
    if (selectedTxnId == null || txns.status !== 'ready') return null;
    return txns.transactions.find((t) => t.id === selectedTxnId) ?? null;
  }, [selectedTxnId, txns]);

  // Color map keyed by parent category name (matches the donut). Includes
  // the code-only categories so the chip colors income/tax-return rows
  // even though they never appear in spending.byCategory.
  const categoryColors = useMemo(() => {
    const rows = dashboard.data?.spending.byCategory ?? [];
    return assignCategoryColors([
      ...rows.map((r) => r.name),
      ...CODE_ONLY_CATEGORIES,
    ]);
  }, [dashboard.data]);

  // Map every budget item name to its parent category name so a transaction
  // categorized as a sub-item (e.g. "Rent") displays — and colors — as its
  // parent (e.g. "Home"). The map is keyed by BOTH the exact sheet
  // `item.name` AND a paren-stripped variant, so a transaction stored
  // as "Mortgage & Property" still resolves to its parent when the
  // sheet's canonical item is "Mortgage & Property (Midwest Loan
  // Services)". The parenthetical is the sheet's disambiguator — it
  // doesn't always travel with transactions tagged by older
  // auto-categorization rules.
  const itemToParent = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of dashboard.data?.budget.categories ?? []) {
      for (const item of c.items) {
        if (!item.name) continue;
        map.set(item.name, c.name);
        const stripped = item.name.replace(/\s*\([^)]*\)/g, '').trim();
        if (stripped && stripped !== item.name && !map.has(stripped)) {
          map.set(stripped, c.name);
        }
        // Also key by the fully-normalized form (emoji + parens stripped,
        // whitespace collapsed, NFC). Catches stored values that drift
        // by hidden whitespace / Unicode form from the sheet's bare
        // version so the chip's isSubItem check still hits.
        const normalized = normalizeCategoryForMatch(item.name);
        if (normalized && !map.has(normalized)) {
          map.set(normalized, c.name);
        }
      }
    }
    return map;
  }, [dashboard.data]);

  // Bare category name → full sheet name (with emoji prefix). The PFC
  // default-mapper writes bare top-level names like "Food & Beverages" into
  // budget_category, but the sheet's canonical version is "🍔 Food &
  // Beverages". Resolving against this map lets the badge inherit the emoji
  // and color even for auto-categorized rows.
  const categoryByBareName = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of dashboard.data?.budget.categories ?? []) {
      map.set(splitCategoryEmoji(c.name).name, c.name);
    }
    return map;
  }, [dashboard.data]);

  // Client-side sort. The server returns transactions newest-first; we
  // re-sort locally so all columns are sortable without re-fetching.
  const sortedTransactions = useMemo(() => {
    if (txns.status !== 'ready') return [];
    const arr = [...txns.transactions];
    const mul = sort.dir === 'asc' ? 1 : -1;
    const categoryFor = (t: Transaction): string => {
      if (t.budgetCategory) {
        return itemToParent.get(t.budgetCategory) ?? t.budgetCategory;
      }
      return 'Uncategorized';
    };
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sort.column) {
        case 'date':
          cmp = a.postedAt.localeCompare(b.postedAt);
          // Stable secondary by id so same-day rows have a deterministic order.
          if (cmp === 0) cmp = a.id - b.id;
          break;
        case 'merchant':
          cmp = (a.merchantName ?? a.name).localeCompare(b.merchantName ?? b.name);
          break;
        case 'account': {
          const aKey = `${a.institutionName ?? ''} ${a.accountName} ${a.accountMask ?? ''}`;
          const bKey = `${b.institutionName ?? ''} ${b.accountName} ${b.accountMask ?? ''}`;
          cmp = aKey.localeCompare(bKey);
          break;
        }
        case 'category':
          cmp = categoryFor(a).localeCompare(categoryFor(b));
          break;
        case 'amount':
          cmp = a.amount - b.amount;
          break;
      }
      return cmp * mul;
    });
    return arr;
  }, [txns, sort, itemToParent]);

  // Optional category filter. Two modes:
  //   - Default: when the filter is a parent category, expand the match
  //     set to include every line-item under that parent. That way
  //     "Home & Pets" in the filter dropdown surfaces transactions
  //     tagged at sub-item names like "Mortgage & Property".
  //   - Exact mode (`?categoryExact=1`): only match the literal value.
  //     The Budget page's "Uncategorized" rollup button sets this so
  //     the count it displays matches the count the user sees on the
  //     filtered list. Item-level filters skip expansion either way
  //     (items are leaves with no children).
  // Match also includes the emoji-prefixed sheet name so legacy rows
  // tagged "🏠 Home & Pets" still hit when filtered by the bare name.
  const categoryFilter = searchParams.get('category');
  const categoryExact = searchParams.get('categoryExact') === '1';
  const shouldFlash = searchParams.get('flash') === '1';
  const allowedFilterCategories = useMemo(() => {
    if (!categoryFilter) return null;
    const set = new Set<string>();
    set.add(categoryFilter);
    const fullName = categoryByBareName.get(categoryFilter);
    if (fullName) set.add(fullName);
    if (!categoryExact && fullName) {
      const cat = dashboard.data?.budget.categories.find(
        (c) => c.name === fullName,
      );
      if (cat) {
        for (const item of cat.items) {
          if (item.name) set.add(item.name);
        }
      }
    }
    return set;
  }, [categoryFilter, categoryExact, categoryByBareName, dashboard.data]);
  const visibleTransactions = useMemo(() => {
    if (!allowedFilterCategories) return sortedTransactions;
    const matches = (cat: string | null): boolean =>
      cat != null && allowedFilterCategories.has(cat);
    return sortedTransactions.filter((t) => {
      if (matches(t.budgetCategory)) return true;
      return (t.splits ?? []).some((s) => matches(s.budgetCategory));
    });
  }, [sortedTransactions, allowedFilterCategories]);

  // When a category filter is active and a transaction matches ONLY
  // through one of its splits, the list should display just that
  // matching split — not the parent transaction (whose own category is
  // something different, often Transfer). Without this, the row's
  // amount and category misrepresent the user's filter ("I want Mortgage
  // & Property transactions" → don't show a $700 Transfer row). Returns
  // an array of {category, amount, splitIndex} pairs; splitIndex is
  // null when the row represents the whole transaction (unsplit or
  // direct match against the transaction's own budgetCategory).
  const buildLines = useMemo(() => {
    const matches = allowedFilterCategories
      ? (cat: string | null) =>
          cat != null && allowedFilterCategories.has(cat)
      : null;
    return (t: Transaction) => {
      const directlyMatches = matches ? matches(t.budgetCategory) : true;
      const allSplits = t.splits ?? [];
      if (matches && !directlyMatches) {
        return allSplits
          .map((s, i) => ({
            category: s.budgetCategory ?? t.budgetCategory,
            amount: s.amount,
            splitIndex: i,
          }))
          .filter((line) => matches(line.category));
      }
      // No filter (or transaction matches at the top level): use the
      // existing 2+ split → multi-row rule, otherwise render a single
      // row from the transaction itself.
      const trueSplits = allSplits.length > 1 ? allSplits : null;
      return trueSplits
        ? trueSplits.map((s, i) => ({
            category: s.budgetCategory ?? t.budgetCategory,
            amount: s.amount,
            splitIndex: i,
          }))
        : [
            {
              category: t.budgetCategory,
              amount: t.amount,
              splitIndex: null as number | null,
            },
          ];
    };
  }, [allowedFilterCategories]);

  // Ref to the first matching row so we can scrollIntoView + flash it when
  // the user lands here via the Budget page's Uncategorized click. The
  // mobile and desktop renderers each call setFirstMatchEl on their own
  // first row; only one is mounted at a time so they don't race.
  const firstMatchRef = useRef<HTMLElement | null>(null);
  const setFirstMatchEl = (el: HTMLElement | null) => {
    firstMatchRef.current = el;
  };

  // Trigger scroll + flash once after the filtered list resolves. Then
  // clear the `flash` param so a later re-render (e.g. month change) doesn't
  // re-fire the animation. We keep the `category` param so the filter
  // persists until the user clicks the budget tab again or clears it.
  useEffect(() => {
    if (!shouldFlash || visibleTransactions.length === 0) return;
    const el = firstMatchRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('row-flash');
    const t = window.setTimeout(() => {
      el.classList.remove('row-flash');
    }, 1600);
    const next = new URLSearchParams(searchParams);
    next.delete('flash');
    setSearchParams(next, { replace: true });
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFlash, visibleTransactions.length]);

  // Reserve a fixed width for the Category column wide enough to fit the
  // longest possible badge label. Computed from every budget item we know
  // about (so navigating months doesn't reflow the table) plus PFC fallback
  // labels for any currently-uncategorized rows. The Category column then
  // stops competing with Merchant for space, and Merchant gets the rest.
  const categoryColumnMinWidth = useMemo(() => {
    const labels: string[] = ['Uncategorized'];
    for (const g of budgetGroups) {
      for (const item of g.items) labels.push(displayCategory(item));
    }
    const maxChars = labels.reduce((m, l) => Math.max(m, l.length), 0);
    // text-xs (~12px) char width ≈ 6.5px + badge dot/gap/padding (~32px) +
    // td horizontal padding (px-4 both sides = 32px). A few px slack so we
    // never sit right at the truncation edge.
    return Math.ceil(maxChars * 6.5 + 32 + 32) + 4;
  }, [budgetGroups]);

  // Same idea for the Account column — lock width to fit the longest
  // possible account label so the column doesn't reflow.
  const accountColumnMinWidth = useMemo(() => {
    if (plaid.status !== 'ready') return 0;
    let maxChars = 0;
    for (const item of plaid.items) {
      for (const a of item.accounts) {
        const label = `${a.name}${a.mask ? ` ••${a.mask}` : ''}`;
        if (label.length > maxChars) maxChars = label.length;
      }
    }
    if (maxChars === 0) return 0;
    // text-xs (~12px) char width ≈ 6.5px + logo (18px) + gap (8px) +
    // td padding (px-4 both sides = 32px).
    return Math.ceil(maxChars * 6.5 + 18 + 8 + 32) + 4;
  }, [plaid]);

  // Account-filter options for the dropdown. "All accounts" first, then
  // every account from this user's Plaid items, each rendered with the
  // institution's logo + account name + mask.
  const accountOptions = useMemo<DropdownOption[]>(() => {
    if (plaid.status !== 'ready') return [{ value: '', label: 'All accounts' }];
    const out: DropdownOption[] = [{ value: '', label: 'All accounts' }];
    for (const item of plaid.items) {
      for (const a of item.accounts) {
        const mask = a.mask ? ` ••${a.mask}` : '';
        const accountLabel = `${a.name}${mask}`;
        out.push({
          value: String(a.id),
          label: `${item.institutionName ?? 'Account'} · ${accountLabel}`,
          display: (
            // Block-level flex (vs inline-flex) so the row fills the
            // option's text slot, and `min-w-0` on both the flex
            // container AND the label span — required for `truncate`
            // inside flex children to actually shrink and ellipsize
            // long names like "Wells Fargo Everyday Checking ••1234".
            <div className="flex items-center gap-1.5 min-w-0">
              {item.institutionName && (
                <span className="shrink-0 inline-flex">
                  <ThirdPartyLogo location={item.institutionName} size={16} />
                </span>
              )}
              <span className="truncate min-w-0">{accountLabel}</span>
            </div>
          ),
        });
      }
    }
    return out;
  }, [plaid]);

  const setAccount = (next: number | null) => {
    const params = new URLSearchParams(searchParams);
    if (next == null) params.delete('account');
    else params.set('account', String(next));
    setSearchParams(params, { replace: true });
  };

  const setCategory = (next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next == null || next === '') {
      params.delete('category');
    } else {
      params.set('category', next);
    }
    // Drop deep-link state whenever the filter changes — the flash glow
    // and the rollup-exact flag both belong to the just-clicked row from
    // the Budget page, not to filters the user picks from the popover.
    params.delete('flash');
    params.delete('categoryExact');
    setSearchParams(params, { replace: true });
  };

  // When embedded, drop the page-container + header (the host page owns
  // those). Body is identical either way; the wrapping <div> + header
  // are conditional. Inlining the conditional via a single ternary
  // around the JSX would force everything to be duplicated, so the
  // body is built once and returned wrapped or bare.
  const body = (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex flex-col gap-1 min-w-0">
            <h2 className="text-2xl font-semibold">{monthLabel}</h2>
            {txns.status === 'ready' && txns.totals.spent > 0 && (
              <span className="tabular text-sm text-fg-tertiary">
                <span className="font-semibold">
                  {formatCurrency(txns.totals.spent, 'USD', 'en-US', 0)}
                </span>
                {' spent'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                // Reset returns to the default current-month, unfiltered
                // view — clear the category deep-link too so the user
                // doesn't see "no transactions" because a stale filter is
                // still narrowing the list.
                const next = new URLSearchParams(searchParams);
                next.delete('month');
                next.delete('category');
                next.delete('categoryExact');
                next.delete('flash');
                setSearchParams(next, { replace: true });
              }}
              disabled={isCurrentMonth && !categoryFilter}
              aria-hidden={isCurrentMonth && !categoryFilter}
              tabIndex={isCurrentMonth && !categoryFilter ? -1 : 0}
              style={{ visibility: isCurrentMonth && !categoryFilter ? 'hidden' : 'visible' }}
              className="px-3 h-8 text-xs font-medium rounded-full border border-border-strong text-fg hover:bg-overlay transition-colors"
            >
              Reset
            </button>
            <div className="flex items-center gap-1.5">
              <IconButton
                onClick={() => setMonth(shiftMonth(monthKey, -1))}
                aria-label="Previous month"
                variant="outline"
                icon={<CaretLeftIcon size={14} weight="bold" />}
              />
              <IconButton
                onClick={() => setMonth(shiftMonth(monthKey, 1))}
                disabled={isCurrentMonth}
                aria-label="Next month"
                variant="outline"
                icon={<CaretRightIcon size={14} weight="bold" />}
              />
            </div>
            <FiltersButton
              accountId={accountId}
              setAccount={setAccount}
              accountOptions={accountOptions}
              categoryFilter={categoryFilter}
              setCategory={setCategory}
              categories={dashboard.data?.budget.categories ?? []}
            />
          </div>
        </div>
        {(categoryFilter || accountId != null) && (
          <div className="flex flex-wrap gap-2">
            {accountId != null && (() => {
              // Find the selected account in the loaded Plaid items so we
              // can render its institution logo and name+mask. Account
              // chips use a neutral white tint instead of a category-style
              // color since accounts aren't color-coded anywhere else in
              // the app.
              if (plaid.status !== 'ready') return null;
              let matchedAccount: typeof plaid.items[number]['accounts'][number] | null = null;
              let matchedInstitution: string | null = null;
              for (const item of plaid.items) {
                for (const a of item.accounts) {
                  if (a.id === accountId) {
                    matchedAccount = a;
                    matchedInstitution = item.institutionName ?? null;
                    break;
                  }
                }
                if (matchedAccount) break;
              }
              if (!matchedAccount) return null;
              const filterColor = 'var(--berg-text)';
              const accountLabel = `${matchedAccount.name}${matchedAccount.mask ? ` ••${matchedAccount.mask}` : ''}`;
              return (
                <div
                  className="inline-flex items-center gap-2 pl-2.5 pr-1 py-1 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: `color-mix(in srgb, ${filterColor} 8%, transparent)`,
                    color: filterColor,
                  }}
                >
                  <span className="truncate inline-flex items-center gap-1.5">
                    {matchedInstitution && (
                      <span className="shrink-0 inline-flex">
                        <ThirdPartyLogo
                          location={matchedInstitution}
                          size={14}
                        />
                      </span>
                    )}
                    <span>{accountLabel}</span>
                  </span>
                  <ClearFilterButton
                    filterColor={filterColor}
                    onClear={() => setAccount(null)}
                  />
                </div>
              );
            })()}
            {categoryFilter && (() => {
              // Resolve the filter value back to its parent so the chip
              // borrows the parent's emoji AND color even when the user
              // drilled in at a line-item. Without this resolution the
              // pill would hash the item name (e.g. "Jewelry Insurance")
              // and land on a pink/red hue that doesn't match the cyan
              // row chips for the same parent (Home & Pets).
              const parentFullName =
                itemToParent.get(categoryFilter) ??
                categoryByBareName.get(categoryFilter) ??
                categoryFilter;
              const { emoji: filterEmoji } = splitCategoryEmoji(parentFullName);
              const filterColor = getCategoryColor(parentFullName);
              // Strip the parenthetical disambiguator the sheet uses on
              // some items (e.g. "Mortgage & Property (Midwest Loan
              // Services)") so the chip reads as the bare label.
              const filterBareName = categoryFilter
                .replace(/\s*\([^)]*\)/g, '')
                .trim();
              return (
                <div
                  className="inline-flex items-center gap-2.5 pl-3 pr-1 py-1 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: `color-mix(in srgb, ${filterColor} 14%, transparent)`,
                    color: filterColor,
                  }}
                >
                  <span className="truncate inline-flex items-baseline gap-2">
                    {filterEmoji && <span aria-hidden>{filterEmoji}</span>}
                    <span>{filterBareName}</span>
                  </span>
                  <ClearFilterButton
                    filterColor={filterColor}
                    onClear={() => {
                      const next = new URLSearchParams(searchParams);
                      next.delete('category');
                      next.delete('categoryExact');
                      next.delete('flash');
                      setSearchParams(next, { replace: true });
                    }}
                  />
                </div>
              );
            })()}
          </div>
        )}
      <div className="data-table hidden sm:block">
        <table>
          <thead>
            <tr className="text-left">
              <th>
                <SortableHeader column="date" sort={sort} onSort={onSortClick}>
                  Date
                </SortableHeader>
              </th>
              <th>
                <SortableHeader column="merchant" sort={sort} onSort={onSortClick}>
                  Merchant
                </SortableHeader>
              </th>
              <th style={accountColumnMinWidth ? { width: accountColumnMinWidth, minWidth: accountColumnMinWidth } : undefined}>
                <SortableHeader column="account" sort={sort} onSort={onSortClick}>
                  Account
                </SortableHeader>
              </th>
              <th style={{ width: categoryColumnMinWidth, minWidth: categoryColumnMinWidth }}>
                <SortableHeader column="category" sort={sort} onSort={onSortClick}>
                  Category
                </SortableHeader>
              </th>
              {/* Dedicated column for the arrows-split marker. Empty for
                  non-split rows, so the icon column stays vertically aligned
                  regardless of how many digits the amount has. */}
              <th className="w-4 px-0" aria-hidden="true" />
              <th className="text-right w-px pl-1!">
                <SortableHeader column="amount" sort={sort} onSort={onSortClick}>
                  Amount
                </SortableHeader>
              </th>
            </tr>
          </thead>
          <tbody>
            {txns.status === 'loading' && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-fg-secondary">
                  Loading…
                </td>
              </tr>
            )}
            {txns.status === 'error' && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-red">
                  {txns.error}
                </td>
              </tr>
            )}
            {txns.status === 'ready' && txns.transactions.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-fg-secondary">
                  {viewingOtherPerson ? (
                    <>
                      {getPersonName(scope as 'dakota' | 'natalia')}'s
                      personal-account transactions aren't visible to you.
                    </>
                  ) : (
                    <>
                      No transactions in {monthLabel}
                      {accountId != null ? ' for this account' : ''}
                    </>
                  )}
                </td>
              </tr>
            )}
            {txns.status === 'ready' &&
              visibleTransactions.flatMap((t, txnIndex) => {
                const description =
                  t.merchantName && t.name !== t.merchantName ? t.name : null;
                // `buildLines` handles both the original multi-row rule
                // (2+ splits) and the filter-aware case: when only a
                // split matches the active category filter, this
                // returns just that split so the visible row reflects
                // what the user asked for.
                const lines = buildLines(t);
                // Two or more visible lines = a real split. We render the
                // date / merchant / account once across the group: first row
                // gets full content, subsequent rows leave those cells blank
                // and the standard full-width row border is suppressed in
                // favor of a partial divider scoped to the category + amount
                // cells. The arrows-split icon next to the category chip
                // signals that the row is part of a multi-line split.
                const isSplit = lines.length > 1;
                const rows: ReactNode[] = [];
                for (let idx = 0; idx < lines.length; idx++) {
                  const line = lines[idx];
                  const lineCategory = line.category;
                  const lineAmount = line.amount;
                  const rowKey = `${t.id}-${line.splitIndex ?? 'whole'}`;
                  // First row of the first visible transaction gets a ref
                  // so the deep-link scroll/flash can find its target.
                  const isFirstMatch = txnIndex === 0 && idx === 0;
                  const isSubSplitRow = isSplit && idx > 0;

                  // Pending rows dim every cell EXCEPT the date pill — the
                  // date is the most important signal for an uncleared
                  // transaction, so it stays at full opacity while the
                  // merchant/account/category/amount fade back. CSS opacity
                  // multiplies down through children, so dimming has to be
                  // applied per-cell rather than on the <tr>.
                  const dimCell = t.pending ? 'opacity-50' : '';
                  rows.push(
                    <tr
                      key={rowKey}
                      ref={isFirstMatch ? setFirstMatchEl : undefined}
                      onClick={() => setSelectedTxnId(t.id)}
                      className={clsx(
                        'cursor-pointer [&>td]:py-1.5',
                        // Drop the natural full-width row border on continuation
                        // rows; the partial border on category + amount cells
                        // takes over. `!` needed because the data-table CSS
                        // `tbody tr` selector outranks a plain utility class.
                        isSubSplitRow && 'border-t-0!',
                      )}
                    >
                      <td className="w-px pr-3 align-middle">
                        {!isSubSplitRow && (
                          <div className="inline-flex w-10 flex-col items-center rounded-md bg-subtle pt-2 pb-1 leading-none">
                            <span className="text-[9px] font-semibold uppercase tracking-wider text-fg-tertiary">
                              {formatDate(new Date(t.postedAt), { month: 'short' })}
                            </span>
                            <span className="tabular text-sm font-semibold text-fg mt-0.5">
                              {formatDate(new Date(t.postedAt), { day: 'numeric' })}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className={clsx('max-w-0', dimCell)}>
                        {!isSubSplitRow && (
                          <div className="min-w-0 min-h-10 flex flex-col justify-center">
                            <div className="flex items-baseline gap-2 min-w-0">
                              <span className="font-medium truncate">
                                {t.merchantName ?? t.name}
                              </span>
                            </div>
                            {description && (
                              <div className="text-xs text-fg-tertiary truncate">
                                {description}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td
                        className={clsx(
                          'text-fg-secondary whitespace-nowrap text-xs',
                          dimCell,
                        )}
                        style={
                          accountColumnMinWidth
                            ? { width: accountColumnMinWidth, minWidth: accountColumnMinWidth }
                            : undefined
                        }
                      >
                        {!isSubSplitRow && (
                          <div className="flex items-center gap-2">
                            {t.institutionName && (
                              <span className="shrink-0 inline-flex">
                                <ThirdPartyLogo
                                  location={t.institutionName}
                                  size={18}
                                />
                              </span>
                            )}
                            <div className="min-w-0">
                              <div>
                                {t.accountName}
                                {t.accountMask ? ` ••${t.accountMask}` : ''}
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                      <td
                        className={clsx(
                          'whitespace-nowrap',
                          dimCell,
                          isSubSplitRow && 'border-t border-border',
                        )}
                        style={{
                          width: categoryColumnMinWidth,
                          minWidth: categoryColumnMinWidth,
                        }}
                      >
                        <div className="min-h-10 flex items-center">
                          <CategoryChip
                            value={lineCategory}
                            colorMap={categoryColors}
                            itemToParent={itemToParent}
                            categoryByBareName={categoryByBareName}
                          />
                        </div>
                      </td>
                      <td
                        className={clsx(
                          'w-4 px-0 align-middle',
                          dimCell,
                          isSubSplitRow && 'border-t border-border',
                        )}
                      >
                        {isSplit && (
                          <div className="min-h-10 flex items-center justify-end">
                            <ArrowsSplitIcon
                              size={14}
                              weight="bold"
                              className="-rotate-90 text-fg-tertiary shrink-0"
                            />
                          </div>
                        )}
                      </td>
                      <td
                        className={clsx(
                          `text-right tabular font-medium whitespace-nowrap w-px pl-1! ${
                            lineAmount < 0 ? 'text-green' : 'text-fg'
                          }`,
                          dimCell,
                          isSubSplitRow && 'border-t border-border',
                        )}
                      >
                        <div className="min-h-10 flex items-center justify-end">
                          {/* Plaid: positive = debit. Display debits as negative for the user. */}
                          {formatCurrency(-lineAmount)}
                        </div>
                      </td>
                    </tr>,
                  );
                }
                return rows;
              })}
          </tbody>
        </table>
      </div>

      {/* Mobile card list — mirrors the desktop table at <sm so the fixed
          column widths don't get clipped by the data-table's overflow:hidden.
          Bleeds edge-to-edge via -mx-4 to cancel the page's mobile padding,
          giving each row the full viewport width minus its own inset. */}
      <div className="sm:hidden -mx-4">
        {txns.status === 'loading' && (
          <div className="py-8 text-center text-sm text-fg-secondary">
            Loading…
          </div>
        )}
        {txns.status === 'error' && (
          <div className="py-8 text-center text-sm text-red">
            {txns.error}
          </div>
        )}
        {txns.status === 'ready' && txns.transactions.length === 0 && (
          <div className="py-8 text-center text-sm text-fg-secondary">
            {viewingOtherPerson ? (
              <>
                {getPersonName(scope as 'dakota' | 'natalia')}'s
                personal-account transactions aren't visible to you.
              </>
            ) : (
              <>
                No transactions in {monthLabel}
                {accountId != null ? ' for this account' : ''}
              </>
            )}
          </div>
        )}
        {txns.status === 'ready' && txns.transactions.length > 0 && (
          <div className="divide-y divide-border border-y border-border">
            {visibleTransactions.flatMap((t, txnIndex) => {
              const description =
                t.merchantName && t.name !== t.merchantName ? t.name : null;
              // Same treatment as desktop: a true split (2+ lines) renders
              // each line as its own standalone-looking card; a filter
              // match against just one split also renders that split
              // alone. `buildLines` encodes both rules.
              const lines = buildLines(t).map((line) => ({
                key: `m-${t.id}-${line.splitIndex ?? 'whole'}`,
                category: line.category,
                amount: line.amount,
              }));
              const isSplit = lines.length > 1;
              // Mirror desktop: when pending, dim everything except the date
              // — CSS opacity multiplies through children, so the dim has to
              // be applied per-element rather than on the button itself.
              const dim = t.pending ? 'opacity-50' : '';
              return lines.map((line, lineIndex) => {
                // First card of the first visible transaction gets a ref so
                // the deep-link scroll/flash can find its target.
                const isFirstMatch = txnIndex === 0 && lineIndex === 0;
                return (
                <button
                  key={line.key}
                  type="button"
                  ref={isFirstMatch ? setFirstMatchEl : undefined}
                  onClick={() => setSelectedTxnId(t.id)}
                  className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-overlay transition-colors"
                >
                  <span
                    className={clsx(
                      'shrink-0 w-8 flex items-center justify-center mt-0.5',
                      dim,
                    )}
                  >
                    {t.institutionName && (
                      <ThirdPartyLogo location={t.institutionName} size={28} />
                    )}
                  </span>
                  <div className="flex-1 min-w-0 flex items-start gap-3">
                    <div className={clsx('flex-1 min-w-0', dim)}>
                      {/* Title + description share one line: rendered as a
                          single truncating block, so the description (sitting
                          at the end) is the first thing to ellipsize when the
                          row is tight. The truncation ellipsis inherits the
                          *parent block's* color (not the inner span's), so
                          we set the block to tertiary and override the
                          merchant back to text-fg — that way the dots match
                          the description color when truncation lands there. */}
                      <div className="text-sm truncate text-fg-tertiary">
                        <span className="font-medium text-fg">
                          {t.merchantName ?? t.name}
                        </span>
                        {description && (
                          <span className="text-xs ml-2">
                            {description}
                          </span>
                        )}
                      </div>
                      {/* Tight when the chip sits directly under the title —
                          on mobile the description is now inline with the
                          title so the chip is always directly below. Spacing
                          here intentionally mirrors the right column's
                          `gap-0.5` so chip and date line up horizontally. */}
                      <div className="mt-0.5">
                        <CategoryChip
                          value={line.category}
                          colorMap={categoryColors}
                          itemToParent={itemToParent}
                          categoryByBareName={categoryByBareName}
                        />
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-0.5">
                      <div className="flex items-center gap-1.5">
                        {isSplit && (
                          <ArrowsSplitIcon
                            size={14}
                            weight="bold"
                            className={clsx('-rotate-90 text-fg-tertiary shrink-0', dim)}
                          />
                        )}
                        <span
                          className={clsx(
                            'tabular font-medium text-sm whitespace-nowrap',
                            line.amount < 0 ? 'text-green' : 'text-fg',
                            dim,
                          )}
                        >
                          {/* Plaid: positive = debit. Display as negative. */}
                          {formatCurrency(-line.amount)}
                        </span>
                      </div>
                      <span className="text-xs text-fg-tertiary whitespace-nowrap mt-1.5">
                        {formatDate(new Date(t.postedAt), {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                </button>
                );
              });
            })}
          </div>
        )}
      </div>
      </div>

      {txns.status === 'ready' && txns.total > txns.transactions.length && (
        <p className="text-xs text-fg-tertiary text-center">
          Showing {txns.transactions.length} of {txns.total} transactions in this range.
        </p>
      )}

      {selectedTxn && (
        <TransactionModal
          transaction={selectedTxn}
          budgetGroups={budgetGroups}
          categoryByBareName={categoryByBareName}
          onClose={() => setSelectedTxnId(null)}
          onSaved={() => {
            setSelectedTxnId(null);
            if (txns.status === 'ready') txns.reload();
            bumpActualsVersion();
          }}
        />
      )}
    </>
  );

  if (embedded) return body;

  return (
    <div className="page-container space-y-8">
      <header className="page-header">
        <div>
          <h1 className="page-title">Transactions</h1>
          <p className="page-subtitle">Activity from your connected accounts</p>
        </div>
      </header>
      {body}
    </div>
  );
}

function SortableHeader({
  column,
  sort,
  onSort,
  children,
}: {
  column: SortColumn;
  sort: { column: SortColumn; dir: SortDir };
  onSort: (column: SortColumn) => void;
  children: React.ReactNode;
}) {
  const active = sort.column === column;
  const Icon = active && sort.dir === 'desc' ? ArrowDownIcon : ArrowUpIcon;
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className="group inline-flex items-center gap-1 hover:text-fg transition-colors uppercase tracking-[inherit] text-[length:inherit] font-[inherit]"
    >
      <span>{children}</span>
      <Icon
        aria-hidden
        size={11}
        weight="bold"
        className={
          active ? 'text-current' : 'text-fg-secondary opacity-30 group-hover:opacity-60'
        }
      />
    </button>
  );
}

// Resolve a stored category value to the sheet's full category name (with
// emoji prefix) so the badge can pick up the same emoji + color as
// everywhere else:
//   - sub-item ("Rent")           → parent category ("🏠 Home")
//   - bare top-level ("Food & Beverages") → "🍔 Food & Beverages"
//   - unknown stored value        → display as-is
function resolveParentLabel(
  stored: string | null,
  itemToParent: Map<string, string>,
  categoryByBareName: Map<string, string>,
): string | null {
  if (!stored) return null;
  const direct = itemToParent.get(stored) ?? categoryByBareName.get(stored);
  if (direct) return direct;
  // Last-resort normalization: strip any leading emoji prefix AND any
  // parenthetical disambiguator from the stored value, then retry the
  // lookup. Pairs with the same normalization applied to the map's
  // keys (see `itemToParent`), so sibling items can resolve to the
  // same parent — and thus the same color — even when the stored tag
  // drifted from the canonical sheet form.
  const normalized = splitCategoryEmoji(stored)
    .name.replace(/\s*\([^)]*\)/g, '')
    .trim();
  if (normalized && normalized !== stored) {
    const norm =
      itemToParent.get(normalized) ?? categoryByBareName.get(normalized);
    if (norm) return norm;
  }
  return stored;
}

function CategoryChip({
  value,
  colorMap,
  itemToParent,
  categoryByBareName,
}: {
  value: string | null;
  colorMap: Map<string, string>;
  itemToParent: Map<string, string>;
  categoryByBareName: Map<string, string>;
}) {
  const parentLabel = resolveParentLabel(value, itemToParent, categoryByBareName);
  // Resolve the chip's color via three fallback steps. (1) Exact match
  // in the page-level colorMap (which includes palette-assigned entries
  // for unknown categories so the chip stays consistent with the donut /
  // breakdowns). (2) Same lookup on the BARE form, since the map's
  // pre-seed is keyed by bare names and a parent with no spending row in
  // `byCategory` wouldn't have its emoji-prefixed entry registered. (3)
  // `getCategoryColor`, which is deterministic per bare name so
  // siblings resolving to the same parent always land on the same hue
  // even when the colorMap path misses entirely.
  const bareParent = parentLabel
    ? splitCategoryEmoji(parentLabel).name
    : null;
  const color = parentLabel
    ? colorMap.get(parentLabel) ??
      (bareParent && bareParent !== parentLabel
        ? colorMap.get(bareParent)
        : undefined) ??
      getCategoryColor(parentLabel)
    : null;
  // When the stored value is a sub-item of a known parent (e.g. "Rent"
  // under "🏠 Home"), show the sub-item's name as the label but keep the
  // parent's emoji + color so each row stays visually anchored to its
  // top-level category. When the stored value already IS the parent
  // (or doesn't map to one), fall back to the parent label as before.
  const isSubItem = !!value && itemToParent.has(value);
  const parentEmoji = parentLabel ? splitCategoryEmoji(parentLabel).emoji : null;
  const displayLabel = !value
    ? 'Uncategorized'
    : isSubItem && parentEmoji
      ? `${parentEmoji} ${displayCategory(value)}`
      : displayCategory(parentLabel ?? value);
  return <CategoryBadge label={displayLabel} color={color} naked />;
}

// X-shaped clear control that sits inside the colored filter chip. The
// chip's background is itself a translucent tint of the category color,
// so a translucent X badge sitting on top of it would blend with the
// chip and lose contrast. We layer a solid canvas (black) base under the
// tint so the X's color reads against pure black, independent of the
// chip's own tint. Hover swaps the tint to red to telegraph the
// destructive action.
function ClearFilterButton({
  filterColor,
  onClear,
}: {
  filterColor: string;
  onClear: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const tint = hovered
    ? 'var(--berg-red-soft)'
    : `color-mix(in srgb, ${filterColor} 28%, transparent)`;
  return (
    <button
      type="button"
      aria-label="Clear filter"
      onClick={onClear}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        // Solid black base + the tint as a linear-gradient overlay so the
        // chip's translucent background can't show through and shift the
        // X's apparent color.
        backgroundColor: 'var(--berg-bg)',
        backgroundImage: `linear-gradient(${tint}, ${tint})`,
        color: hovered ? 'var(--berg-red)' : undefined,
      }}
      className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors cursor-pointer"
    >
      <XIcon size={13} weight="bold" />
    </button>
  );
}


// Combined filter modal: one icon button collapses the previous "All
// accounts" select and the new category picker into a single
// affordance. A small green dot on the button signals active filters
// at a glance so the user doesn't need to open the modal to check.
// The Modal trigger gives the lists real estate to breathe — long
// category hierarchies render comfortably without forcing the user
// to scroll inside a cramped popover.
function FiltersButton({
  accountId,
  setAccount,
  accountOptions,
  categoryFilter,
  setCategory,
  categories,
}: {
  accountId: number | null;
  setAccount: (next: number | null) => void;
  accountOptions: DropdownOption[];
  categoryFilter: string | null;
  setCategory: (next: string | null) => void;
  /** Raw budget categories from the sheet — used to build the
   * multi-column grid layout, where each card holds one parent
   * category plus its line items. */
  categories: BudgetCategory[];
}) {
  const [open, setOpen] = useState(false);

  const activeCount =
    (accountId != null ? 1 : 0) + (categoryFilter != null ? 1 : 0);

  // Don't render at all if there's nothing to filter — accounts list is
  // just "All accounts" AND categories haven't loaded yet AND nothing
  // is filtered. Once data arrives we always show the button.
  const hasAccountChoices = accountOptions.length > 1;
  const hasCategoryChoices = categories.length > 0;
  if (!hasAccountChoices && !hasCategoryChoices && activeCount === 0) return null;

  const clearAll = () => {
    if (accountId != null) setAccount(null);
    if (categoryFilter != null) setCategory(null);
  };

  return (
    <>
      <div className="relative">
        <IconButton
          onClick={() => setOpen(true)}
          variant="outline"
          aria-label="Filter transactions"
          aria-expanded={open}
          icon={<FunnelSimpleIcon size={14} weight="bold" />}
        />
        {activeCount > 0 && (
          <span
            aria-hidden
            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green border border-canvas"
          />
        )}
      </div>
      {open && (
        <Modal
          onClose={() => setOpen(false)}
          size="full"
          // Cap to the visible viewport (Modal's outer wrapper already
          // adds p-4, hence -2rem) and hide the card's own overflow so
          // the scrollable body inside doesn't bleed past the rounded
          // corners. The body div below picks up the scroll via flex-1
          // + overflow-y-auto — that way header and footer stay pinned
          // and the user can always reach the Done button.
          className="max-h-[calc(100vh-2rem)] overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-none">
            <h2 className="text-xl font-semibold">Filters</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close filters"
              className="w-8 h-8 inline-flex items-center justify-center rounded-full text-fg-tertiary hover:text-fg hover:bg-overlay transition-colors"
            >
              <XIcon size={16} weight="bold" />
            </button>
          </div>
          {/* Side-by-side on tablet+: Account holds the first column,
              Category spans the rest (2 cols at sm, 3 at lg) so its
              own grid of parent-group cards can flow into 2-3 columns.
              On mobile the whole grid collapses to one column and the
              sections stack. The body scrolls as one inside the
              viewport-capped modal so header + footer stay pinned. */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
            <div className="grid gap-5 grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {hasAccountChoices && (
                <div className="sm:col-span-1">
                  <FilterSection
                    label="Account"
                    options={accountOptions}
                    selectedValue={accountId == null ? '' : String(accountId)}
                    onSelect={(v) =>
                      setAccount(v === '' ? null : Number(v))
                    }
                  />
                </div>
              )}
              {hasCategoryChoices && (
                <div
                  className={clsx(
                    hasAccountChoices
                      ? 'sm:col-span-2 md:col-span-3 lg:col-span-4'
                      : 'sm:col-span-3 md:col-span-4 lg:col-span-5',
                  )}
                >
                  <CategoryGridSection
                    categories={categories}
                    selectedValue={categoryFilter ?? ''}
                    onSelect={(v) => setCategory(v === '' ? null : v)}
                  />
                </div>
              )}
            </div>
          </div>
          <ModalFooter align="between">
            <button
              type="button"
              onClick={clearAll}
              disabled={activeCount === 0}
              className="btn btn-secondary btn-sm disabled:opacity-40"
            >
              Clear all
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="btn btn-primary btn-sm"
            >
              Done
            </button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}

// One section inside the Filters modal — a labeled list of options
// where the current selection shows a checkmark. `h-full` lets the
// section stretch to match its sibling column's height inside the
// grid layout.
function FilterSection({
  label,
  options,
  selectedValue,
  onSelect,
}: {
  label: string;
  options: DropdownOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="h-full flex flex-col rounded-xl border border-border bg-canvas overflow-hidden">
      <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-fg-tertiary uppercase tracking-wider">
        {label}
      </div>
      <div>
        {options.map((opt) => {
          const selected = opt.value === selectedValue;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onSelect(opt.value)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-overlay transition-colors"
            >
              {/* min-w-0 lets the slot shrink below its intrinsic
                  content width inside the parent flex, so the display
                  JSX's own truncate (on the inner label) can engage. */}
              <span className="flex-1 min-w-0 overflow-hidden">
                {opt.display ?? opt.label}
              </span>
              {selected && (
                <CheckIcon
                  size={14}
                  weight="bold"
                  className="text-green flex-none"
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Multi-column category picker. Each card holds one parent category
// (clickable header) plus its line items (each clickable). The grid
// flows 1 column on phones, growing to 4 columns on wide screens so
// the user can scan every parent category at a glance. "All
// categories" is pinned above the grid as a quick reset.
function CategoryGridSection({
  categories,
  selectedValue,
  onSelect,
}: {
  categories: BudgetCategory[];
  selectedValue: string;
  onSelect: (value: string) => void;
}) {
  type Group = {
    parentValue: string;
    parentBareLabel: string;
    emoji: string | null;
    items: { value: string; label: string }[];
  };
  const groups = useMemo<Group[]>(() => {
    return categories.map((c) => {
      const { emoji, name: bareName } = splitCategoryEmoji(c.name);
      return {
        parentValue: bareName,
        parentBareLabel: bareName,
        emoji,
        items: c.items
          .filter((i) => i.name)
          .map((i) => ({
            value: i.name,
            label: i.name.replace(/\s*\([^)]*\)/g, '').trim(),
          }))
          .filter((i) => i.label),
      };
    });
  }, [categories]);

  return (
    <div className="h-full flex flex-col rounded-xl border border-border bg-canvas overflow-hidden">
      <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-fg-tertiary uppercase tracking-wider">
        Category
      </div>
      <div className="p-2 flex flex-col gap-2">
        {/* "All categories" pinned at the top as the quick reset. The
            button hugs its content so the checkmark sits flush with
            the label (vs trailing the full row width like the parent
            group buttons do). */}
        <button
          type="button"
          onClick={() => onSelect('')}
          className="self-start inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-left rounded-lg hover:bg-overlay transition-colors"
        >
          <span className="truncate">All categories</span>
          {selectedValue === '' && (
            <CheckIcon
              size={14}
              weight="bold"
              className="text-green flex-none"
            />
          )}
        </button>
        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {groups.map((g) => {
            const parentSelected = g.parentValue === selectedValue;
            return (
              <div
                key={g.parentValue}
                className="flex flex-col"
              >
                <button
                  type="button"
                  onClick={() => onSelect(g.parentValue)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-left hover:bg-overlay transition-colors"
                >
                  {/* Fixed-width emoji slot so the parent's label
                      always starts at the same x-offset whether the
                      sheet category has an emoji or not. Sub-item
                      buttons mirror this offset via `pl-10`
                      (px-3 + emoji slot + gap-2 = 12 + 20 + 8 = 40px). */}
                  <span
                    aria-hidden
                    className="w-5 inline-flex items-center justify-center shrink-0"
                  >
                    {g.emoji}
                  </span>
                  <span className="flex-1 min-w-0 truncate">
                    {g.parentBareLabel}
                  </span>
                  {parentSelected && (
                    <CheckIcon
                      size={14}
                      weight="bold"
                      className="text-green flex-none"
                    />
                  )}
                </button>
                {g.items.length > 0 && (
                  <div className="flex flex-col">
                    {g.items.map((item) => {
                      const itemSelected = item.value === selectedValue;
                      return (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => onSelect(item.value)}
                          className="w-full flex items-center gap-2 pl-10 pr-3 py-1.5 text-sm text-fg-secondary text-left hover:bg-overlay transition-colors"
                        >
                          <span className="flex-1 min-w-0 truncate">
                            {item.label}
                          </span>
                          {itemSelected && (
                            <CheckIcon
                              size={12}
                              weight="bold"
                              className="text-green flex-none"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface TransactionModalProps {
  transaction: Transaction;
  budgetGroups: { category: string; items: string[] }[];
  categoryByBareName: Map<string, string>;
  onClose: () => void;
  onSaved: () => void;
}

// Round to cents so the auto-calc remainder is always representable in
// NUMERIC(14, 2). Without this, JS float math leaves stray digits like
// 350 - 0.1 = 349.9 → 349.90000000000003, which the API would reject.
function roundCents(n: number): number {
  return Math.round(n * 100) / 100;
}

interface SplitLine {
  amount: number;
  budgetCategory: string;
}

function SplitAmountInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (next: number) => void;
}) {
  // Local draft mirrors the input string so the user can type partial values
  // ("-", "1.") without the numeric round-trip stamping over their keystroke.
  const [draft, setDraft] = useState<string>(value.toFixed(2));
  useEffect(() => {
    setDraft(value.toFixed(2));
  }, [value]);
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-subtle focus-within:border-border-strong transition-colors w-32">
      <span className="text-fg-tertiary tabular">$</span>
      <input
        type="number"
        inputMode="decimal"
        step="0.01"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const parsed = parseFloat(draft);
          if (Number.isFinite(parsed)) onChange(parsed);
          else setDraft(value.toFixed(2));
        }}
        className="flex-1 min-w-0 bg-transparent outline-none tabular text-sm text-fg"
      />
    </div>
  );
}

function SplitLineEditor({
  line,
  budgetGroups,
  categoryByBareName,
  onChangeAmount,
  onChangeCategory,
  onRemove,
  canRemove,
}: {
  line: SplitLine;
  budgetGroups: { category: string; items: string[] }[];
  categoryByBareName: Map<string, string>;
  onChangeAmount: (next: number) => void;
  onChangeCategory: (next: string) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const valueInOptions =
    (CODE_ONLY_CATEGORIES as readonly string[]).includes(line.budgetCategory) ||
    budgetGroups.some((g) => g.items.includes(line.budgetCategory));
  const orphanLabel =
    line.budgetCategory !== '' && !valueInOptions
      ? displayCategory(categoryByBareName.get(line.budgetCategory) ?? line.budgetCategory)
      : null;

  return (
    <div className="flex items-center gap-2">
      <SplitAmountInput value={line.amount} onChange={onChangeAmount} />
      <Select
        value={line.budgetCategory}
        onChange={(e) => onChangeCategory(e.target.value)}
        className="flex-1 min-w-0"
      >
        <option value="">Uncategorized</option>
        {CODE_ONLY_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
        {orphanLabel && <option value={line.budgetCategory}>{orphanLabel}</option>}
        {budgetGroups.map((g) => (
          <optgroup key={g.category} label={displayCategory(g.category)}>
            {g.items.map((i) => (
              <option key={i} value={i}>
                {displayCategory(i)}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove split line"
          className="shrink-0 w-9 h-9 flex items-center justify-center rounded-md text-fg-tertiary hover:text-red hover:bg-overlay text-xl leading-none transition-colors"
          title="Remove line"
        >
          ×
        </button>
      )}
    </div>
  );
}

function TransactionModal({
  transaction,
  budgetGroups,
  categoryByBareName,
  onClose,
  onSaved,
}: TransactionModalProps) {
  const [value, setValue] = useState<string>(
    canonicalizeCategory(transaction.budgetCategory ?? '', budgetGroups),
  );

  // Auto-categorized rows store a bare top-level PFC name (e.g. "Food &
  // Beverages") that doesn't match any sub-item option in the select. Surface
  // it as a synthetic option so the dropdown shows the current state instead
  // of silently falling back to "Uncategorized". Code-only categories
  // (Income, Tax Return) are real options so they don't count as orphans.
  const valueInOptions =
    (CODE_ONLY_CATEGORIES as readonly string[]).includes(value) ||
    budgetGroups.some((g) => g.items.includes(value));
  const orphanLabel =
    value !== '' && !valueInOptions
      ? displayCategory(categoryByBareName.get(value) ?? value)
      : null;
  const [remember, setRemember] = useState<boolean>(false);
  // Optional amount restriction on the saved rule. When enabled, the rule
  // only auto-applies to future txns whose absolute amount matches this
  // txn's. Useful for ambiguous merchants (e.g. "Apple Services" → iCloud
  // at $0.99 vs. AppleCare at $4.16). Disabled when `remember` is off.
  const [restrictAmount, setRestrictAmount] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Split state. The "Split this" checkbox carves the transaction across
  // N budget categories. The N-line editor auto-balances the LAST line so
  // the sum always equals transaction.amount when N === 2 — no error
  // states possible. 3+ lines is free-edit; the user reaches a balanced
  // sum on their own and the splits section surfaces the remainder +
  // disables Save when off.

  // Derive initial mode from existing splits on the transaction.
  const initial = useMemo<{
    splitEnabled: boolean;
    lines: SplitLine[];
  }>(() => {
    const existing = transaction.splits ?? [];

    // 2+ splits → split mode.
    if (existing.length >= 2) {
      return {
        splitEnabled: true,
        lines: existing.map((s) => ({
          amount: roundCents(s.amount),
          budgetCategory: canonicalizeCategory(s.budgetCategory ?? '', budgetGroups),
        })),
      };
    }

    // Default seed when the user enables Split mode later — 2 balanced lines
    // inheriting the transaction's current category.
    const half = roundCents(transaction.amount / 2);
    const seededCategory = canonicalizeCategory(
      transaction.budgetCategory ?? '',
      budgetGroups,
    );
    const seededLines: SplitLine[] = [
      { amount: half, budgetCategory: seededCategory },
      {
        amount: roundCents(transaction.amount - half),
        budgetCategory: seededCategory,
      },
    ];

    return { splitEnabled: false, lines: seededLines };
  }, [
    transaction.splits,
    transaction.amount,
    transaction.budgetCategory,
    budgetGroups,
  ]);

  const [splitEnabled, setSplitEnabled] = useState<boolean>(initial.splitEnabled);
  const [splitLines, setSplitLines] = useState<SplitLine[]>(initial.lines);
  const [rememberSplit, setRememberSplit] = useState<boolean>(true);

  const inSplitMode = splitEnabled;
  const hadSplitOnOpen = (transaction.splits ?? []).length >= 1;

  // If `initial` recomputes after mount (e.g. transaction prop changes),
  // re-seed local state to match.
  useEffect(() => {
    setSplitEnabled(initial.splitEnabled);
    setSplitLines(initial.lines);
  }, [initial]);

  // 2-line case auto-balances the other line whenever an amount changes —
  // there's no ambiguity about who absorbs the delta. 3+ lines is free-edit;
  // the user reaches SUM(amounts) === transaction.amount on their own and
  // the splits section surfaces the remainder + disables Save when off.
  const updateLine = (i: number, patch: Partial<SplitLine>) => {
    setSplitLines((prev) => {
      const next = prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
      if ('amount' in patch && next.length === 2) {
        const otherIdx = i === 0 ? 1 : 0;
        next[otherIdx] = {
          ...next[otherIdx],
          amount: roundCents(transaction.amount - next[i].amount),
        };
      }
      return next;
    });
  };

  const addLine = () => {
    setSplitLines((prev) => {
      // Pre-fill the new line with whatever's left to allocate so the
      // common case ("I added one more split for the remainder") is a
      // single click, while still letting the user override.
      const used = prev.reduce((acc, l) => acc + l.amount, 0);
      const remainder = roundCents(transaction.amount - used);
      return [...prev, { amount: remainder, budgetCategory: '' }];
    });
  };

  const removeLine = (i: number) => {
    setSplitLines((prev) => {
      if (prev.length <= 2) return prev;
      return prev.filter((_, idx) => idx !== i);
    });
  };

  // Penny-tolerance balance check; mirrors the server's cents-comparison.
  const splitSum = splitLines.reduce((acc, l) => acc + l.amount, 0);
  const splitRemaining = roundCents(transaction.amount - splitSum);
  const splitBalanced = Math.abs(splitRemaining) < 0.005;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Decide what splits payload (if any) to write. Two cases:
      //   - In Split mode: write N split lines, clear parent category.
      //   - Otherwise (was split, now isn't): DELETE existing splits.
      type SplitPost = { method: 'POST'; lines: SplitLine[]; createRule: boolean };
      type SplitDelete = { method: 'DELETE' };
      let splitAction: SplitPost | SplitDelete | null = null;

      if (inSplitMode) {
        splitAction = {
          method: 'POST',
          lines: splitLines,
          createRule: rememberSplit,
        };
      } else if (hadSplitOnOpen) {
        splitAction = { method: 'DELETE' };
      }

      // Recategorize the parent. In Split mode the parent's category is
      // cleared (each line carries its own).
      const willCreateRule = !inSplitMode && remember && value !== '';
      const recatRes = await apiFetch('/api/transactions/recategorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transactionId: transaction.id,
          budgetCategory: inSplitMode ? null : value === '' ? null : value,
          createRule: willCreateRule,
          // Only forward the amount when we're actually creating a rule —
          // recategorize won't read this field otherwise. Use abs() since
          // the server always stores positive amounts (refunds are <0).
          ruleAmount:
            willCreateRule && restrictAmount
              ? Math.abs(transaction.amount)
              : undefined,
        }),
      });
      if (!recatRes.ok) {
        const body = await recatRes.text().catch(() => '');
        throw new Error(`recategorize ${recatRes.status}: ${body.slice(0, 200)}`);
      }

      if (splitAction?.method === 'POST') {
        const splitRes = await apiFetch('/api/transactions/split', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transactionId: transaction.id,
            splits: splitAction.lines.map((l) => ({
              amount: l.amount,
              budgetCategory: l.budgetCategory === '' ? null : l.budgetCategory,
            })),
            createRule: splitAction.createRule,
          }),
        });
        if (!splitRes.ok) {
          const body = await splitRes.text().catch(() => '');
          throw new Error(`split ${splitRes.status}: ${body.slice(0, 200)}`);
        }
      } else if (splitAction?.method === 'DELETE') {
        const splitRes = await apiFetch('/api/transactions/split', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transactionId: transaction.id }),
        });
        if (!splitRes.ok) {
          const body = await splitRes.text().catch(() => '');
          throw new Error(`split-delete ${splitRes.status}: ${body.slice(0, 200)}`);
        }
      }

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed');
    } finally {
      setSaving(false);
    }
  };

  const description =
    transaction.merchantName && transaction.name !== transaction.merchantName
      ? transaction.name
      : null;
  const isCredit = transaction.amount < 0;

  return (
    <Modal onClose={onClose}>
      <div className="p-5 flex justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {transaction.institutionName && (
            <span className="shrink-0 mt-0.5">
              <ThirdPartyLogo location={transaction.institutionName} size={32} />
            </span>
          )}
          <div className="min-w-0 flex flex-col gap-1">
            <div className="text-base font-semibold leading-tight">
              {transaction.merchantName ?? transaction.name}
            </div>
            <div className="text-xs text-fg-tertiary flex items-center gap-1.5 flex-wrap">
              <span>
                {transaction.accountName}
                {transaction.accountMask ? ` ••${transaction.accountMask}` : ''}
              </span>
              {transaction.pending && <span>· pending</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end justify-between gap-1 shrink-0">
          <div
            className={`tabular text-base font-semibold leading-tight whitespace-nowrap ${
              isCredit ? 'text-green' : 'text-fg'
            }`}
          >
            {formatCurrency(-transaction.amount)}
          </div>
          <div className="text-xs text-fg-tertiary tabular">
            {formatDate(new Date(transaction.postedAt), {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </div>
        </div>
      </div>

      {description && (
        <div className="px-5 pb-4 -mt-1 text-xs text-fg-tertiary truncate">
          {description}
        </div>
      )}

      {!inSplitMode && (
        <div className="border-t border-border p-6 flex flex-col gap-3">
          <div className="text-xs uppercase tracking-[0.08em] font-medium text-fg-tertiary">
            Category
          </div>
          <Select autoFocus value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="">Uncategorized</option>
            {CODE_ONLY_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
            {orphanLabel && <option value={value}>{orphanLabel}</option>}
            {budgetGroups.map((g) => (
              <optgroup key={g.category} label={displayCategory(g.category)}>
                {g.items.map((i) => (
                  <option key={i} value={i}>
                    {displayCategory(i)}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          <Checkbox
            checked={remember}
            onChange={setRemember}
            label="Remember for similar transactions"
          />
          {remember && (
            <div className="pl-6">
              <Checkbox
                checked={restrictAmount}
                onChange={setRestrictAmount}
                label={`Only include transactions for ${formatCurrency(Math.abs(transaction.amount))}`}
              />
            </div>
          )}
        </div>
      )}

      {inSplitMode && (
        <div className="border-t border-border p-6 flex flex-col gap-3">
          <div className="text-xs uppercase tracking-[0.08em] font-medium text-fg-tertiary">
            Split lines
          </div>
          {splitLines.map((line, i) => (
            <SplitLineEditor
              key={i}
              line={line}
              budgetGroups={budgetGroups}
              categoryByBareName={categoryByBareName}
              onChangeAmount={(next) => updateLine(i, { amount: roundCents(next) })}
              onChangeCategory={(next) => updateLine(i, { budgetCategory: next })}
              onRemove={() => removeLine(i)}
              canRemove={splitLines.length > 2}
            />
          ))}
          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={addLine}
              className="text-xs text-fg-secondary hover:text-fg"
            >
              + Add split line
            </button>
            <span
              className={`text-xs tabular ${
                splitBalanced ? 'text-fg-tertiary' : 'text-red'
              }`}
            >
              {splitBalanced
                ? `Total ${formatCurrency(transaction.amount)}`
                : `${formatCurrency(splitSum)} of ${formatCurrency(transaction.amount)} · ${
                    splitRemaining > 0 ? '+' : ''
                  }${formatCurrency(splitRemaining)} unallocated`}
            </span>
          </div>
          <Checkbox
            checked={rememberSplit}
            onChange={setRememberSplit}
            label="Remember this split for similar transactions"
          />
        </div>
      )}

      {error && (
        <div className="px-6 pb-3 -mt-3">
          <p className="text-xs text-red">{error}</p>
        </div>
      )}

      <ModalFooter align="between">
        <div className="flex items-center min-w-0">
          <Checkbox
            checked={splitEnabled}
            onChange={setSplitEnabled}
            label="Split this"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn btn-outline"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || (inSplitMode && !splitBalanced)}
            className="btn btn-primary"
            title={
              inSplitMode && !splitBalanced
                ? `Splits must sum to ${formatCurrency(transaction.amount)}`
                : undefined
            }
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
